import React, {createContext, useContext, useState} from "react";
import dayjs, {Dayjs} from "dayjs";
import UserLoginContext from "./userLoginContext";
import {getName} from "../utility/utility";
import server from "../server";
import {PunchesData} from "../domains/models/attendance/attendance";

type PunchContextType = {
    isLoading: boolean;
    isCheckedIn: boolean;
    fetchPunchRecords: (
        employeeId: string | undefined,
        startDate: Dayjs,
        endDate: Dayjs,
        page?: number,
        size?: number
    ) => Promise<void>;
    records: PunchesData[];
    markCheckIn: () => Promise<void>;
    markCheckOut: () => Promise<void>;
    clearPunchRecords: () => void;
};

interface IPunchState {
    records: PunchesData[],
    isLoading: boolean,
    isCheckedIn: boolean,
}

const PunchContext = createContext<PunchContextType | null>(null);

export const usePunchContext = () => {
    const context = useContext(PunchContext);
    if (!context) throw new Error("usePunchContext must be used within PunchProvider");
    return context;
};

export const PunchProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {user} = useContext(UserLoginContext);
    const isAdmin = user.isAdmin;
    const [punchState, setPunchState] = useState<IPunchState>({
        records: [],
        isLoading: false,
        isCheckedIn: false,
    })

    const clearPunchRecords = () => {
        // empty array = no records
        setPunchState((prev) => ({...prev, range: []}))

    };

    const getDateFormat = (date: Dayjs) => (
        date.format('YYYY-MM-DD')
    )

    const fetchPunchRecords = async (
        employeeId: string | undefined,
        startDate: Dayjs,
        endDate: Dayjs,
        page: number = 0,
        size: number = 10
    ) => {
        setPunchState((prev) => ({
            ...prev,
            isLoading: true
        }));
        try {
            let isCheckedIn = false;
            let response: any;
            const today = getDateFormat(dayjs());
            if (isAdmin && !employeeId) {
                response = await server.attendance.admin.punchList(
                    getDateFormat(startDate),
                    getDateFormat(endDate),
                    page,
                    size
                )
            } else if (isAdmin && employeeId) {
                response = await server.attendance.admin.getEmployeePunches(
                    employeeId,
                    getDateFormat(startDate),
                    getDateFormat(endDate),
                    page,
                    size
                );
            } else {
                response = await server.attendance.employee.punchList(
                    employeeId!,
                    page,
                    size,
                    getDateFormat(startDate),
                    getDateFormat(endDate),
                );
            }


            const records: PunchesData[] = response.map(({name, ...item}: any) => ({
                ...item,
                name: name,
                employeeName: getName(name.firstName, name.middleName, name.lastName),
            })) || [];

            //punch records come sorted by latest at first
            const latestPunch = records[0] || null;

            if (latestPunch && latestPunch?.date !== today) {
                isCheckedIn = false;
            } else {
                isCheckedIn = latestPunch?.type === "CHECK_IN";
            }

            // return sorted list of punches acc to the date and time [ oldest->latest ]
            setPunchState((prev) => ({
                ...prev,
                records: records,
                isCheckedIn: isCheckedIn,
            }));

        } catch (error) {
            console.error("Error fetching range punch records:", error);
        } finally {
            setPunchState((prev) => ({
                ...prev,
                isLoading: false
            }));
        }
    };

    // function sortPunches(arr: PunchesData[]) {
    //     return ([...arr].sort(
    //         (a, b) =>
    //             dayjs(`${a.date}T${a.time}`).valueOf() -
    //             dayjs(`${b.date}T${b.time}`).valueOf()
    //     ));
    //
    // }

    const markCheckIn = async () => {

        if (!user?.employeeId) return;

        setPunchState((prev) => ({...prev, isLoading: true}));

        const today = dayjs();

        try {
            await server.employee.checkIn(user.employeeId);
            setPunchState((prev) => ({...prev, isCheckedIn: true}));
            await fetchPunchRecords(user.employeeId, today, today);
        } catch (error) {
            console.error("Error during check-in:", error);
            throw error; // re-throw so outer function knows
        } finally {
            setPunchState((prev) => ({...prev, isLoading: false}));
        }
    };

    const markCheckOut = async () => {
        if (!user?.employeeId) return;
        setPunchState((prev) => ({...prev, isLoading: true}));
        const today = dayjs();
        try {
            await server.employee.checkOut(user.employeeId);
            setPunchState((prev) => ({...prev, isCheckedIn: false}));
            await fetchPunchRecords(user.employeeId, today, today);
        } catch (error) {
            console.error("Error during check-out:", error);
            throw error; // re-throw so outer function knows
        } finally {
            setPunchState((prev) => ({...prev, isLoading: false}));
        }
    };


    return (
        <PunchContext.Provider
            value={{
                records: punchState.records,
                isLoading: punchState.isLoading,
                isCheckedIn: punchState.isCheckedIn,
                fetchPunchRecords,
                markCheckIn,
                markCheckOut,
                clearPunchRecords,
            }}
        >
            {children}
        </PunchContext.Provider>
    );
};
